import { AiOutlineDownload } from "react-icons/ai";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import alertService from "../../utils/alertService";
import customerService from "../../services/customerService";
import companyService from "../../services/companyService";
import yardService from "../../services/yardService";
import lotService from "../../services/lotService";
import stockCategoryService from "../../services/stockCategoryService";
import { validateFileSize, validateFileType } from "../../utils/validators";
import { numberToWords } from "../../utils/numberToWords";
import { useMasterModals } from "../../hooks/useMasterModals";
import { formatDate } from "../../utils/dateUtils";

// PDF Generation Imports
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import TextArea from "../../components/common/TextArea";

const toApiDateTime = (value) => {
  if (!value) return new Date().toISOString();
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? new Date(value).toISOString()
    : date.toISOString();
};

const GST_OPTIONS = [0, 5, 12, 15, 18, 20, 21, 28];
const GST_DROPDOWN_OPTIONS = [
  ...GST_OPTIONS.map((opt) => ({ value: opt, label: `${opt}%` })),
  { value: "custom", label: "Custom..." },
];

const calculateSellingMaterialTotals = (material) => {
  const quantity = Number(material.quantity) || 0;
  const ratePerKg = Number(material.ratePerKg) || 0;
  const amount = Number((quantity * ratePerKg).toFixed(2));
  const gstPercentage = Number(material.gstPercentage) || 0;
  const gstAmount = Number(((amount * gstPercentage) / 100).toFixed(2));
  const grandTotal = Number((amount + gstAmount).toFixed(2));

  return {
    amount,
    gstPercentage,
    gstAmount,
    grandTotal,
  };
};

const buildSellingMaterialPayload = (
  material,
  sellingMasterId,
  transactionDate,
) => {
  const totals = calculateSellingMaterialTotals(material);

  return {
    sellingMaterialsId: material.sellingMaterialsId
      ? String(material.sellingMaterialsId)
      : undefined,
    sellingMasterId: String(sellingMasterId),
    yardId: material.yardId ? String(material.yardId) : null,
    lotId: material.lotId ? String(material.lotId) : null,
    stockCategoryId: material.stockCategoryId
      ? String(material.stockCategoryId)
      : null,
    quantity: Number(material.quantity),
    ratePerKg: Number(material.ratePerKg),
    amount: totals.amount,
    gstPercentage: totals.gstPercentage,
    gstAmount: totals.gstAmount,
    grandTotal: totals.grandTotal,
    description: material.description || "",
    transactionDate: toApiDateTime(transactionDate),
  };
};

const FilePreviewField = ({
  label,
  value,
  onChange,
  error,
  accept = ".pdf,.jpg,.jpeg,.png",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const isFile = value instanceof File;
  const hasValue = Boolean(value);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      onChange(file);
    }
  };

  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
        {label}
      </label>
      {hasValue ? (
        <div className="flex items-center gap-2 p-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <div className="flex-1 truncate pl-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
            {isFile ? value.name : "View Uploaded File"}
          </div>
          <div className="flex items-center gap-1">
            <a
              href={isFile ? URL.createObjectURL(value) : value}
              target="_blank"
              rel="noopener noreferrer"
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-primary-600 shadow-sm hover:text-primary-700 transition-all border border-slate-100 dark:border-slate-600"
              title="View File"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </a>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-rose-500 shadow-sm hover:text-rose-600 transition-all border border-slate-100 dark:border-slate-600"
              title="Clear selection"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div
          className="relative group"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={(e) => onChange(e.target.files[0])}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div
            className={`flex items-center gap-3 px-3 py-1.5 rounded-lg border border-dashed transition-all ${
              isDragging
                ? "border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 scale-[1.02] shadow-sm"
                : "border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 group-hover:border-primary-500"
            }`}
          >
            <div
              className={`h-7 w-7 flex items-center justify-center rounded-md shadow-sm transition-colors border ${
                isDragging
                  ? "bg-primary-500 text-white border-primary-600"
                  : "bg-white dark:bg-slate-700 text-slate-400 group-hover:text-primary-500 border-slate-100 dark:border-slate-600"
              }`}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <span
              className={`text-[11px] font-bold uppercase tracking-tight transition-colors ${
                isDragging
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {isDragging ? "Drop File Here" : "Upload Invoice"}
            </span>
          </div>
        </div>
      )}
      {error && (
        <p className="text-[10px] font-bold text-rose-500 uppercase tracking-tight mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

function SellingMasterFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();

  const basePath = useMemo(() => {
    if (location.pathname.includes("/invoice-management/selling-master"))
      return "/invoice-management/selling-master";
    return "/selling-master";
  }, [location.pathname]);

  const isEditPage = Boolean(id);
  const formMode = useMemo(() => {
    if (!isEditPage) return "create";
    return location.pathname.includes("/view") ? "view" : "edit";
  }, [isEditPage, location.pathname]);

  // --- States ---
  const [formData, setFormData] = useState({
    invoiceNo: "",
    date: new Date().toISOString().split("T")[0],
    customerId: "",
    companyId: "",
    deliveryDate: "",
    dueDate: "",
    termsOfPayment: "",
    vehicleNo: "",
    gstAmount: 0,
    roundOffAmount: 0,
    totalAmount: 0, // This is subtotal in backend
    grandTotal: 0,
    isActive: true,
    memo: "",

    // Snapshot fields - Customer
    customerCode: "",
    customerName: "",
    mobileNo: "",
    customerEmail: "",
    address: "",
    country: "",

    // Snapshot fields - Company
    companyName: "",
    companyAddress: "",
    companyGSTNo: "",
    stateName: "",
    stateCode: "",
    companyEmail: "",
    websiteLink: "",
    isReceived: false,
  });

  const [attachment, setAttachment] = useState(null);

  const [materials, setMaterials] = useState([
    {
      yardId: "",
      lotId: "",
      stockCategoryId: "",
      quantity: "",
      availableQuantity: 0,
      rowLotOptions: [],
      rowLotData: [],
      ratePerKg: "",
      amount: 0,
      gstPercentage: 18,
      gstAmount: 0,
      grandTotal: 0,
      description: "",
    },
  ]);
  const [options, setOptions] = useState({
    customers: [],
    companies: [],
    yards: [],
    lots: [],
    stockCategories: [],
  });

  // Store full objects for auto-filling
  const [fullOptions, setFullOptions] = useState({
    customers: [],
    companies: [],
    lots: [],
  });

  const termsOfPaymentOptions = [
    { label: "1 Day", value: "1" },
    { label: "15 Days", value: "15" },
    { label: "30 Days", value: "30" },
    { label: "45 Days", value: "45" },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const isLocked =
    isEditPage && formMode !== "view" && Boolean(formData.isReceived);

  // --- Combined Initial Load ---
  const fetchInitialData = useCallback(
    async (skipLoader = false) => {
      if (!skipLoader) setIsLoading(true);
      try {
        const [customers, companies, yards, lots, categoriesRes] =
          await Promise.all([
            customerService.getActiveCustomers(),
            companyService.getActiveCompanies(),
            yardService.getActiveYards(),
            lotService.getActiveLots({ showLocked: true }),
            apiClient.get("/selling-materials/stock-categories/active"),
          ]);

        const cData = Array.isArray(customers)
          ? customers
          : customers?.items || [];
        const compData = Array.isArray(companies)
          ? companies.items || companies
          : companies?.items || [];
        const allLots = Array.isArray(lots) ? lots : lots?.items || [];

        const categoriesData =
          categoriesRes?.data?.data || categoriesRes?.data || [];
        const categoriesItems = Array.isArray(categoriesData)
          ? categoriesData
          : categoriesData.items || [];

        setFullOptions({
          customers: cData,
          companies: compData,
          lots: allLots,
        });

        const initialOptions = {
          customers: cData.map((c) => ({
            label: `${c.customerName} (${c.customerCode})`,
            value: c.customerId,
          })),
          companies: compData.map((c) => ({
            label: c.companyName,
            value: c.id || c.companyId,
          })),
          yards: (yards.items || yards).map((y) => ({
            label: y.yardNo,
            value: y.yardId,
          })),
          lots: allLots.map((l) => ({
            label: l.lotNo,
            value: l.lotId,
          })),
          stockCategories: categoriesItems.map((s) => ({
            label: s.stockCategoryName,
            value: s.stockCategoryId,
          })),
        };
        setOptions(initialOptions);

        // Auto-fill company details for new record
        if (!isEditPage && compData.length > 0) {
          const company = compData[0];
          setFormData((prev) => ({
            ...prev,
            companyId: company.id || company.companyId,
            companyName: company.companyName || "",
            companyAddress: company.companyAddress || "",
            companyGSTNo: company.companyGSTNo || "",
            stateName: company.stateName || "",
            stateCode: company.stateCode || "",
            companyEmail: company.email || "",
            websiteLink: company.websiteLink || "",
          }));
        }

        // If Edit Mode, fetch the record
        if (isEditPage && id) {
          const response = await apiClient.get(`/selling-master/${id}`);
          const data = response?.data?.data || response?.data || {};

          // Re-calculate options to include saved values even if they are inactive
          if (Array.isArray(data.sellingMaterials)) {
            data.sellingMaterials.forEach((m) => {
              if (
                m.yardId &&
                !initialOptions.yards.find(
                  (opt) => String(opt.value) === String(m.yardId),
                )
              ) {
                initialOptions.yards.push({
                  label: m.yardNo || "Unknown Yard",
                  value: m.yardId,
                });
              }
              if (
                m.lotId &&
                !initialOptions.lots.find(
                  (opt) => String(opt.value) === String(m.lotId),
                )
              ) {
                initialOptions.lots.push({
                  label: m.lotNo || "Unknown Lot",
                  value: m.lotId,
                });
              }
              if (
                m.stockCategoryId &&
                !initialOptions.stockCategories.find(
                  (opt) => String(opt.value) === String(m.stockCategoryId),
                )
              ) {
                initialOptions.stockCategories.push({
                  label: m.stockCategoryName || "Unknown Category",
                  value: m.stockCategoryId,
                });
              }
            });
            setOptions(initialOptions);
          }

          setFormData({
            invoiceNo: data.invoiceNo || "",
            date: data.date ? data.date.split("T")[0] : "",
            customerId: data.customerId || "",
            companyId: data.companyId || "",
            deliveryDate: data.deliveryDate
              ? data.deliveryDate.split("T")[0]
              : "",
            dueDate: data.dueDate ? data.dueDate.split("T")[0] : "",
            termsOfPayment: data.termsOfPayment || "",
            vehicleNo: data.vehicleNo || "",
            gstAmount: data.gstAmount || 0,
            roundOffAmount: data.roundOffAmount || 0,
            totalAmount: data.totalAmount || 0,
            grandTotal: data.grandTotal || 0,
            isActive: data.isActive !== false,
            memo: data.memo || "",

            customerCode: data.customerCode || "",
            customerName: data.customerName || "",
            mobileNo: data.mobileNo || "",
            customerEmail: data.customerEmail || "",
            address: data.address || "",
            country: data.country || "",

            companyName: data.companyName || "",
            companyAddress: data.companyAddress || "",
            companyGSTNo: data.companyGSTNo || "",
            stateName: data.stateName || "",
            stateCode: data.stateCode || "",
            companyEmail: data.companyEmail || "",
            websiteLink: data.websiteLink || "",
            isReceived: Boolean(data.isReceived),
          });

          if (data.attachmentLink) {
            setAttachment(data.attachmentLink);
          }

          if (Array.isArray(data.sellingMaterials)) {
            const loadedMaterials = await Promise.all(
              data.sellingMaterials.map(async (m) => {
                let availableQuantity = 0;
                let rowLotOptions = [];
                let rowLotData = [];
                let isLocked = false;

                if (m.stockCategoryId) {
                  try {
                    const lots = await lotService.getStockSummaryByCategory(
                      m.stockCategoryId,
                    );

                    // Fetch full lot details to check lock status
                    const lotDetails = allLots.find(
                      (l) => String(l.lotId) === String(m.lotId),
                    );
                    isLocked = lotDetails?.isLocked || false;

                    // Make sure the currently saved lot is in the list
                    if (
                      m.lotId &&
                      !lots.some((l) => String(l.lotId) === String(m.lotId))
                    ) {
                      lots.push({
                        lotId: m.lotId,
                        lotNo: lotDetails?.lotNo || m.lotNo || "Unknown Lot",
                        yardId: m.yardId || lotDetails?.yardId,
                        yardNo:
                          m.yardNo || lotDetails?.yardNo || "Unknown Yard",
                        remainingWeight: 0,
                        currentStock: 0,
                      });
                    }

                    rowLotData = lots;
                    rowLotOptions = lots.map((l) => {
                      const stock = Number(
                        l.currentStock ?? l.remainingWeight ?? 0,
                      ).toLocaleString("en-IN", { minimumFractionDigits: 2 });
                      return {
                        label: `${l.lotNo} (${stock} KG)`,
                        value: l.lotId,
                      };
                    });

                    const selectedLot = lots.find(
                      (l) => String(l.lotId) === String(m.lotId),
                    );

                    const currentPhysicalStock = selectedLot
                      ? parseFloat(
                          selectedLot.currentStock ??
                            selectedLot.remainingWeight ??
                            0,
                        )
                      : 0;

                    availableQuantity =
                      currentPhysicalStock + parseFloat(m.quantity || 0);
                  } catch (err) {
                    console.error(
                      "Failed to fetch stock summary for edit",
                      err,
                    );
                    availableQuantity = parseFloat(m.quantity || 0);
                  }
                }

                return {
                  sellingMaterialsId: m.sellingMaterialsId,
                  yardId: m.yardId || "",
                  lotId: m.lotId || "",
                  originalLotId: m.lotId || "",
                  stockCategoryId: m.stockCategoryId || "",
                  originalStockCategoryId: m.stockCategoryId || "",
                  quantity: m.quantity || 0,
                  originalQuantity: m.quantity || 0,
                  ratePerKg: m.ratePerKg || 0,
                  amount: m.amount || 0,
                  gstPercentage:
                    m.gstPercentage !== undefined && m.gstPercentage !== null
                      ? m.gstPercentage
                      : 18,
                  isCustomGst: !GST_OPTIONS.includes(
                    Number(
                      m.gstPercentage !== undefined && m.gstPercentage !== null
                        ? m.gstPercentage
                        : 18,
                    ),
                  ),
                  gstAmount:
                    m.gstAmount || calculateSellingMaterialTotals(m).gstAmount,
                  grandTotal:
                    m.grandTotal ||
                    calculateSellingMaterialTotals(m).grandTotal,
                  description: m.description || "",
                  availableQuantity,
                  rowLotOptions,
                  rowLotData,
                  isLocked,
                };
              }),
            );
            setMaterials(loadedMaterials);
          }
        }
      } catch (err) {
        console.error("Failed to load initial data", err);
        setFormError(normalizeApiError(err).message);
      } finally {
        setIsLoading(false);
      }
    },
    [id, isEditPage],
  );

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const { openModal, renderModals } = useMasterModals({
    customer: () => fetchInitialData(true),
    company: () => fetchInitialData(true),
    yard: () => fetchInitialData(true),
    lot: () => fetchInitialData(true),
    stockCategory: () => fetchInitialData(true),
  });

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    if (type === "number") {
      const numVal = parseFloat(value);
      if (!isNaN(numVal) && numVal < 0) {
        finalValue = 0;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Auto-fill customer details
    if (name === "customerId") {
      const customer = fullOptions.customers.find(
        (c) => String(c.customerId) === String(value),
      );
      if (customer) {
        setFormData((prev) => ({
          ...prev,
          customerId: value,
          customerCode: customer.customerCode || "",
          customerName: customer.customerName || "",
          mobileNo: customer.mobileNo || "",
          customerEmail: customer.email || "",
          address: customer.address || "",
          country: customer.country?.countryName || "",
        }));
      }
    }

    // Auto-fill company details
    if (name === "companyId") {
      const company = fullOptions.companies.find(
        (c) => String(c.id || c.companyId) === String(value),
      );
      if (company) {
        setFormData((prev) => ({
          ...prev,
          companyId: value,
          companyName: company.companyName || "",
          companyAddress: company.companyAddress || "",
          companyGSTNo: company.companyGSTNo || "",
          stateName: company.stateName || "",
          stateCode: company.stateCode || "",
          companyEmail: company.email || "",
          websiteLink: company.websiteLink || "",
        }));
      }
    }
  };

  // --- Auto-calculate Due Date ---
  useEffect(() => {
    if (formData.date && formData.termsOfPayment) {
      const days = parseInt(formData.termsOfPayment);
      if (!isNaN(days)) {
        const invoiceDate = new Date(formData.date);
        invoiceDate.setDate(invoiceDate.getDate() + days);
        const calculatedDate = invoiceDate.toISOString().split("T")[0];
        setFormData((prev) => ({ ...prev, dueDate: calculatedDate }));
      }
    }
  }, [formData.date, formData.termsOfPayment]);

  const handleMaterialChange = async (index, field, value) => {
    const updated = [...materials];
    let finalValue = value;

    if (
      field === "quantity" ||
      field === "ratePerKg" ||
      field === "gstPercentage"
    ) {
      const numVal = parseFloat(value);
      if (!isNaN(numVal) && numVal < 0) {
        finalValue = 0;
      }

      if (field === "quantity" && !isNaN(numVal)) {
        const avail = parseFloat(updated[index].availableQuantity) || 0;
        if (numVal > avail) {
          finalValue = avail;
          alertService.warning(
            `Quantity cannot exceed available stock (${avail} KG)`,
          );
        }
      }

      if (field === "gstPercentage" && !isNaN(numVal)) {
        if (numVal > 100) {
          finalValue = 100;
          alertService.warning("GST percentage cannot exceed 100%");
        }
      }
    }

    updated[index][field] = finalValue;

    if (field === "stockCategoryId") {
      const catId = value;
      updated[index].lotId = "";
      updated[index].yardId = "";
      updated[index].availableQuantity = 0;
      updated[index].rowLotOptions = [];
      updated[index].rowLotData = [];

      if (catId) {
        try {
          const lots = await lotService.getStockSummaryByCategory(catId);
          updated[index].rowLotData = lots;
          updated[index].rowLotOptions = lots.map((l) => {
            const stock = Number(
              l.currentStock ?? l.remainingWeight ?? 0,
            ).toLocaleString("en-IN", { minimumFractionDigits: 2 });
            return {
              label: `${l.lotNo} ( ${stock} KG)`,
              value: l.lotId,
            };
          });
        } catch (err) {
          console.error("Failed to fetch lots by category", err);
          updated[index].rowLotOptions = [];
          updated[index].rowLotData = [];
        }
      }
    }

    if (field === "lotId") {
      const lotId = value;
      if (lotId) {
        const selectedLot = updated[index].rowLotData?.find(
          (l) => String(l.lotId) === String(lotId),
        );

        if (selectedLot) {
          updated[index].yardId = selectedLot.yardId || "";

          const lotDetails = fullOptions.lots.find(
            (l) => String(l.lotId) === String(lotId),
          );
          const isLocked = lotDetails?.isLocked || false;
          updated[index].isLocked = isLocked;

          const currentPhysicalStock = parseFloat(
            selectedLot.currentStock ?? selectedLot.remainingWeight ?? 0,
          );

          const hasSavedId = Boolean(updated[index].sellingMaterialsId);
          const originalQuantity = parseFloat(
            updated[index].originalQuantity || 0,
          );

          const isSameLotAndCategory =
            hasSavedId &&
            String(lotId) === String(updated[index].originalLotId) &&
            String(updated[index].stockCategoryId) ===
              String(updated[index].originalStockCategoryId);

          updated[index].availableQuantity =
            currentPhysicalStock +
            (isSameLotAndCategory ? originalQuantity : 0);
        } else {
          updated[index].yardId = "";
          updated[index].availableQuantity = 0;
        }
      } else {
        updated[index].yardId = "";
        updated[index].availableQuantity = 0;
      }
    }

    if (
      field === "quantity" ||
      field === "ratePerKg" ||
      field === "gstPercentage"
    ) {
      const totals = calculateSellingMaterialTotals(updated[index]);
      updated[index].amount = totals.amount.toFixed(2);
      updated[index].gstAmount = totals.gstAmount.toFixed(2);
      updated[index].grandTotal = totals.grandTotal.toFixed(2);
    }

    setMaterials(updated);
  };

  const addMaterial = () => {
    setMaterials([
      ...materials,
      {
        yardId: "",
        lotId: "",
        stockCategoryId: "",
        quantity: "",
        availableQuantity: 0,
        rowLotOptions: [],
        rowLotData: [],
        ratePerKg: "",
        amount: 0,
        gstPercentage: 18,
        gstAmount: 0,
        grandTotal: 0,
        description: "",
      },
    ]);
  };

  const removeMaterial = async (index) => {
    const material = materials[index];
    const hasId = Boolean(material.sellingMaterialsId);

    // Prevent deleting the last saved item — backend requires at least one material.
    // Check saved rows count (not total rows), so adding an empty row doesn't bypass this guard.
    const savedMaterialsCount = materials.filter((m) =>
      Boolean(m.sellingMaterialsId),
    ).length;
    if (hasId && savedMaterialsCount === 1) {
      alertService.warning(
        "At least one selling material is required. You cannot delete the last saved item.",
      );
      return;
    }

    if (hasId) {
      const result = await alertService.deleteConfirm("this item");
      if (!result.isConfirmed) return;

      try {
        await apiClient.delete(
          `/selling-materials/${material.sellingMaterialsId}`,
        );
        alertService.success("Item deleted successfully.");
      } catch (err) {
        alertService.error(normalizeApiError(err).message);
        return;
      }
    }

    const updated = materials.filter((_, i) => i !== index);
    setMaterials(updated);
  };

  // --- Auto-calculate Totals from material rows, Round Off ---
  useEffect(() => {
    const subtotal = materials.reduce(
      (sum, m) => sum + calculateSellingMaterialTotals(m).amount,
      0,
    );

    const gst = materials.reduce(
      (sum, m) => sum + calculateSellingMaterialTotals(m).gstAmount,
      0,
    );

    const rawTotal = subtotal + gst;
    const roundedTotal = Math.round(rawTotal);
    const roundOff = (roundedTotal - rawTotal).toFixed(2);

    setFormData((prev) => ({
      ...prev,
      gstAmount: gst.toFixed(2),
      roundOffAmount: roundOff,
      totalAmount: subtotal.toFixed(2),
      grandTotal: roundedTotal.toFixed(2),
    }));
  }, [materials]);

  const validate = () => {
    const errors = {};
    if (!formData.invoiceNo) errors.invoiceNo = "Invoice No is required";
    if (!formData.customerId) errors.customerId = "Customer is required";
    if (!formData.companyId) errors.companyId = "Company is required";
    if (!formData.date) errors.date = "Date is required";

    if (fieldErrors.attachment) {
      errors.attachment = fieldErrors.attachment;
    }

    if (materials.length === 0) {
      alertService.error("At least one selling material is required");
      return false;
    }

    // Check for zero quantity or zero amount in materials, and ensure Stock Category and Lot are selected
    const hasInvalidMaterial = materials.some(
      (m) =>
        !m.stockCategoryId ||
        !m.lotId ||
        !m.quantity ||
        parseFloat(m.quantity) <= 0 ||
        !m.ratePerKg ||
        parseFloat(m.ratePerKg) <= 0 ||
        m.gstPercentage === "" ||
        parseFloat(m.gstPercentage) < 0,
    );

    if (hasInvalidMaterial) {
      alertService.error(
        "All materials must have Stock Category, Lot, quantity, rate, and GST percentage.",
      );
      return false;
    }

    if (parseFloat(formData.grandTotal) <= 0) {
      alertService.error("Grand total must be greater than zero.");
      return false;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  function generateInvoicePDF(masterId, isPreview = false) {
    try {
      const doc = new jsPDF({ compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;

      // ─── Color Palette ───
      const C_PRIMARY   = [197, 160, 89];   // Elegant golden
      const C_LIGHT_BG  = [250, 247, 237]; // Light cream tint matching gold
      const C_GRAY      = [110, 120, 135]; // muted gray for secondary text
      const C_DARK      = [30,  40,  55];  // near-black for body text
      const C_LINE      = [225, 215, 195]; // subtle gold-tinted border/line color

      // ─── Outer Border ───
      doc.setDrawColor(...C_PRIMARY);
      doc.setLineWidth(0.5);
      doc.rect(margin, margin, contentWidth, pageHeight - margin * 2);

      // ─── Top Header Bar ───
      doc.setFillColor(...C_PRIMARY);
      doc.rect(margin, margin, contentWidth, 13, "F");

      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("TAX INVOICE", pageWidth / 2, margin + 9, { align: "center" });

      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text("Original for Buyer", margin + contentWidth - 3, margin + 9, { align: "right" });

      // ─── Logo + Invoice Meta ───
      let curY = margin + 16;
      try {
        doc.addImage("/logo/png/logo-01.png", "PNG", margin + 2, curY, 42, 13);
      } catch (e) {}

      // Vertical divider after logo
      doc.setDrawColor(...C_LINE);
      doc.setLineWidth(0.3);
      doc.line(margin + 48, curY - 1, margin + 48, curY + 18);

      // Meta key-value pairs (two columns)
      const m1X = margin + 51;
      const m1V = margin + 82;
      const m2X = margin + 115;
      const m2V = margin + 148;

      const drawMeta = (label, value, x, vx, y) => {
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...C_GRAY);
        doc.text(label, x, y);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...C_DARK);
        doc.text(value || "-", vx, y);
      };

      drawMeta("Invoice No :", formData.invoiceNo, m1X, m1V, curY + 5);
      drawMeta("Date :", formatDate(formData.date), m1X, m1V, curY + 11);
      drawMeta("Vehicle No :", formData.vehicleNo, m2X, m2V, curY + 5);
      drawMeta("Delivery Date :", formatDate(formData.deliveryDate), m2X, m2V, curY + 11);

      curY += 20;

      // ─── Section divider ───
      doc.setDrawColor(...C_PRIMARY);
      doc.setLineWidth(0.4);
      doc.line(margin, curY, margin + contentWidth, curY);

      curY += 3;

      // ─── FROM / BILL TO Headers ───
      const halfW = (contentWidth - 3) / 2;

      doc.setFillColor(...C_LIGHT_BG);
      doc.rect(margin, curY, halfW, 5, "F");
      doc.rect(margin + halfW + 3, curY, halfW, 5, "F");

      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_PRIMARY);
      doc.text("FROM (SELLER)", margin + 3, curY + 3.5);
      doc.text("BILL TO (CONSIGNEE)", margin + halfW + 6, curY + 3.5);

      curY += 7;

      // Company name & customer name
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_DARK);
      doc.text(formData.companyName || "N/A", margin + 3, curY);
      doc.text(formData.customerName || "N/A", margin + halfW + 6, curY);

      curY += 4.5;
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C_GRAY);

      const compAddr = doc.splitTextToSize(formData.companyAddress || "", halfW - 4);
      doc.text(compAddr, margin + 3, curY);
      const custAddr = doc.splitTextToSize(formData.address || "", halfW - 4);
      doc.text(custAddr, margin + halfW + 6, curY);

      const addrH = Math.max(compAddr.length, custAddr.length) * 3.5;
      curY += addrH + 2;

      doc.setFontSize(7.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_DARK);
      doc.text(`GSTIN : ${formData.companyGSTNo || "-"}`, margin + 3, curY);
      doc.text(`Mobile : ${formData.mobileNo || "-"}`, margin + halfW + 6, curY);
      curY += 4.5;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C_GRAY);
      doc.text(`${formData.companyEmail || "-"}`, margin + 3, curY);
      doc.text(`${formData.customerEmail || "-"}`, margin + halfW + 6, curY);

      curY += 3;

      // Vertical divider between address columns
      doc.setDrawColor(...C_LINE);
      doc.setLineWidth(0.3);
      doc.line(margin + halfW + 1.5, margin + 36, margin + halfW + 1.5, curY);

      // Horizontal divider
      doc.setDrawColor(...C_PRIMARY);
      doc.setLineWidth(0.4);
      doc.line(margin, curY, margin + contentWidth, curY);
      curY += 2;

      // ─── Materials Table (with per-item GST columns) ───
      const tableBody = materials.map((m, i) => {
        const lot =
          options.lots.find((l) => String(l.value) === String(m.lotId))?.label || "N/A";
        const category =
          options.stockCategories.find(
            (s) => String(s.value) === String(m.stockCategoryId),
          )?.label || "N/A";
        const desc = m.description ? `\n(${m.description})` : "";
        const totals = calculateSellingMaterialTotals(m);

        return [
          i + 1,
          `${lot} - ${category}${desc}`,
          Number(m.quantity).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          "KG",
          Number(m.ratePerKg).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          Number(totals.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          `${Number(m.gstPercentage || 0)}%`,
          Number(totals.gstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          Number(totals.grandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        ];
      });

      const totalQty     = materials.reduce((s, m) => s + (parseFloat(m.quantity) || 0), 0);
      const totalTaxable = materials.reduce((s, m) => s + calculateSellingMaterialTotals(m).amount, 0);
      const totalGst     = materials.reduce((s, m) => s + calculateSellingMaterialTotals(m).gstAmount, 0);
      const totalGrand   = materials.reduce((s, m) => s + calculateSellingMaterialTotals(m).grandTotal, 0);

      autoTable(doc, {
        startY: curY,
        head: [
          ["Sr.", "Description of Goods", "Qty", "Unit", "Rate/KG", "Value (Rs.)", "GST%", "GST Amt (Rs.)", "Total (Rs.)"],
        ],
        body: tableBody,
        foot: [
          [
            "",
            "TOTAL",
            totalQty.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
            "KG",
            "",
            Number(totalTaxable).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
            "",
            Number(totalGst).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
            Number(totalGrand).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          ],
        ],
        headStyles: {
          fillColor: C_PRIMARY,
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: "bold",
          halign: "center",
          cellPadding: 2.5,
          lineWidth: 0,
        },
        footStyles: {
          fillColor: C_LIGHT_BG,
          textColor: C_DARK,
          fontSize: 7.5,
          fontStyle: "bold",
          halign: "right",
          lineColor: C_LINE,
          lineWidth: 0.15,
        },
        alternateRowStyles: {
          fillColor: [248, 250, 255],
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 8 },
          1: { halign: "left" },
          2: { halign: "right", cellWidth: 20 },
          3: { halign: "center", cellWidth: 10 },
          4: { halign: "right", cellWidth: 20 },
          5: { halign: "right", cellWidth: 26 },
          6: { halign: "center", cellWidth: 13 },
          7: { halign: "right", cellWidth: 24 },
          8: { halign: "right", cellWidth: 25 },
        },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
          lineColor: C_LINE,
          lineWidth: 0.15,
          textColor: C_DARK,
          font: "helvetica",
        },
        margin: { left: margin, right: margin },
      });

      let finalY = doc.lastAutoTable.finalY + 3;

      // ─── GST Breakup by rate (grouped) ───
      const gstGroups = {};
      materials.forEach((m) => {
        const rate = Number(m.gstPercentage || 0);
        const totals = calculateSellingMaterialTotals(m);
        if (!gstGroups[rate]) gstGroups[rate] = { taxable: 0, gst: 0 };
        gstGroups[rate].taxable += totals.amount;
        gstGroups[rate].gst     += totals.gstAmount;
      });

      // ─── Summary Section ───
      // Estimate height: base rows + one row per distinct GST rate
      const gstRateCount    = Object.keys(gstGroups).length;
      const summaryRowsH    = (5 + gstRateCount) * 5 + 20; // approx
      const amountInWordsH  = 26;
      const totalSummaryH   = Math.max(summaryRowsH, amountInWordsH) + 8;

      if (finalY + totalSummaryH > pageHeight - margin - 35) {
        doc.addPage();
        finalY = margin + 5;
        // Redraw outer border on new page
        doc.setDrawColor(...C_PRIMARY);
        doc.setLineWidth(0.5);
        doc.rect(margin, margin, contentWidth, pageHeight - margin * 2);
      }

      // Left panel: amount in words
      const leftPanelW = contentWidth * 0.58;
      const rightPanelW = contentWidth - leftPanelW - 1;
      const rightPanelX = margin + leftPanelW + 1;

      // Summary box (right)
      doc.setDrawColor(...C_LINE);
      doc.setLineWidth(0.3);
      doc.rect(rightPanelX, finalY, rightPanelW, totalSummaryH);

      // Summary box header
      doc.setFillColor(...C_PRIMARY);
      doc.rect(rightPanelX, finalY, rightPanelW, 6, "F");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("BILLING SUMMARY", rightPanelX + rightPanelW / 2, finalY + 4.2, { align: "center" });

      let sY = finalY + 9;
      const sLabelX = rightPanelX + 3;
      const sValX   = rightPanelX + rightPanelW - 3;

      const drawSummaryRow = (label, value, bold = false, color = C_DARK) => {
        doc.setFontSize(7.5);
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setTextColor(...color);
        doc.text(label, sLabelX, sY);
        doc.text(value, sValX, sY, { align: "right" });
        sY += 5;
      };

      drawSummaryRow(
        "Taxable Amount :",
        `${Number(formData.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      );

      // Per-rate GST rows
      Object.entries(gstGroups)
        .sort(([a], [b]) => Number(a) - Number(b))
        .forEach(([rate, data]) => {
          drawSummaryRow(
            `Output IGST @ ${rate}% :`,
            `${Number(data.gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
          );
        });

      // Thin divider
      doc.setDrawColor(...C_LINE);
      doc.setLineWidth(0.2);
      doc.line(sLabelX, sY, sValX, sY);
      sY += 3;

      drawSummaryRow(
        "Total GST :",
        `${Number(formData.gstAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        true,
      );
      drawSummaryRow(
        "Round Off :",
        `${Number(formData.roundOffAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      );

      doc.setDrawColor(...C_PRIMARY);
      doc.setLineWidth(0.3);
      doc.line(sLabelX, sY, sValX, sY);
      sY += 4;

      // Grand Total row
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_PRIMARY);
      doc.text("Grand Total :", sLabelX, sY);
      doc.text(
        `INR ${Number(formData.grandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        sValX,
        sY,
        { align: "right" },
      );

      // Left panel: amount in words
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_DARK);
      doc.text("Amount Chargeable (in words):", margin + 3, finalY + 9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C_GRAY);
      const words     = numberToWords(Number(formData.grandTotal));
      const splitWords = doc.splitTextToSize(`INR ${words} Only`, leftPanelW - 6);
      doc.text(splitWords, margin + 3, finalY + 14);

      // ─── Footer: Declaration + Signature ───
      let footY = finalY + totalSummaryH + 3;
      if (footY + 30 > pageHeight - margin) {
        doc.addPage();
        footY = margin + 5;
        doc.setDrawColor(...C_PRIMARY);
        doc.setLineWidth(0.5);
        doc.rect(margin, margin, contentWidth, pageHeight - margin * 2);
      }

      const footHalfW = contentWidth / 2 - 2;
      doc.setDrawColor(...C_LINE);
      doc.setLineWidth(0.3);
      doc.rect(margin, footY, footHalfW, 28);
      doc.rect(margin + footHalfW + 4, footY, footHalfW, 28);

      // Declaration
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_DARK);
      doc.text("Declaration:", margin + 3, footY + 5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C_GRAY);
      const declaration =
        "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.";
      doc.text(doc.splitTextToSize(declaration, footHalfW - 6), margin + 3, footY + 9);

      // Signatory
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_DARK);
      doc.text(
        `For, ${formData.companyName || "Our Company"}`,
        margin + contentWidth - 5,
        footY + 5,
        { align: "right" },
      );
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C_GRAY);
      doc.text("Authorized Signatory", margin + contentWidth - 5, footY + 24, { align: "right" });

      // ─── Bottom note ───
      doc.setFontSize(6.5);
      doc.setTextColor(...C_GRAY);
      doc.text(
        "This is a system-generated invoice. Subject to realization of Cheque. Disputes subject to local jurisdiction only.",
        pageWidth / 2,
        pageHeight - margin - 3,
        { align: "center" },
      );

      if (isPreview) {
        return doc.output("blob");
      } else {
        doc.save(`Invoice_${formData.invoiceNo || masterId}.pdf`);
      }
    } catch (err) {
      console.error("Failed to generate PDF", err);
      alertService.error("Failed to generate invoice PDF.");
    }
  }

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const handlePreview = () => {
    if (!validate()) return;
    setJustSaved(false);
    const blob = generateInvoicePDF(null, true);
    if (blob) {
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreviewModal(true);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (isLocked) {
      alertService.info(
        "This invoice is already received and cannot be edited.",
      );
      return;
    }
    if (!validate()) return;

    setIsSaving(true);
    setFormError(null);
    try {
      // 1. Prepare FormData for the Selling Master POST or PATCH call
      const fd = new FormData();
      if (formData.invoiceNo) fd.append("invoiceNo", formData.invoiceNo);
      if (formData.date) fd.append("date", formData.date);
      fd.append("customerId", String(formData.customerId));
      if (formData.companyId)
        fd.append("companyId", String(formData.companyId));
      if (formData.deliveryDate)
        fd.append("deliveryDate", formData.deliveryDate);
      if (formData.dueDate) fd.append("dueDate", formData.dueDate);
      if (formData.termsOfPayment)
        fd.append("termsOfPayment", formData.termsOfPayment);
      if (formData.vehicleNo) fd.append("vehicleNo", formData.vehicleNo);
      fd.append("gstAmount", String(formData.gstAmount));
      fd.append("roundOffAmount", String(formData.roundOffAmount));
      fd.append("totalAmount", String(formData.totalAmount));
      fd.append("grandTotal", String(formData.grandTotal));
      fd.append("isActive", String(formData.isActive));
      fd.append("memo", formData.memo || "");

      // Append snapshot fields
      fd.append("customerCode", formData.customerCode);
      fd.append("customerName", formData.customerName);
      fd.append("mobileNo", formData.mobileNo);
      fd.append("customerEmail", formData.customerEmail);
      fd.append("address", formData.address);
      fd.append("country", formData.country);

      fd.append("companyName", formData.companyName);
      fd.append("companyAddress", formData.companyAddress);
      fd.append("companyGSTNo", formData.companyGSTNo);
      fd.append("stateName", formData.stateName);
      fd.append("stateCode", formData.stateCode);
      fd.append("companyEmail", formData.companyEmail);
      fd.append("websiteLink", formData.websiteLink);

      if (attachment instanceof File) {
        fd.append("attachment", attachment);
      } else if (!attachment) {
        fd.append("attachmentPath", "");
      }

      // Generate and append the invoice PDF to FormData
      const reportBlob = generateInvoicePDF(null, true);
      if (reportBlob) {
        fd.append(
          "generatedReport",
          reportBlob,
          `Invoice_${formData.invoiceNo || "Draft"}.pdf`,
        );
      }



      const config = { headers: { "Content-Type": "multipart/form-data" } };
      let savedId = id;

      if (formMode === "edit") {
        await apiClient.patch(`/selling-master/${id}`, fd, config);
      } else {
        const response = await apiClient.post("/selling-master", fd, config);
        const responseData = response?.data?.data || response?.data || {};
        savedId = responseData.sellingMasterId;
      }

      // 2. Save materials using the bulk-upsert API.
      const materialsPayload = materials.map((m) =>
        buildSellingMaterialPayload(m, savedId, formData.date),
      );
      await apiClient.post("/selling-materials/bulk-upsert", {
        materials: materialsPayload,
      });

      if (formMode === "edit") {
        alertService.success("Invoice updated successfully.");
      } else {
        alertService.success("Invoice created successfully.");
      }

      navigate(basePath);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    } finally {
      setIsSaving(false);
    }
  };

  const getFilteredCategoryOptions = (idx, m) => {
    return options.stockCategories || [];
  };

  const getFilteredLotOptions = (idx, m) => {
    if (!m.stockCategoryId) return [];
    const baseOpts = m.rowLotOptions || [];

    const otherSelectedLotIds = materials
      .filter(
        (row, i) =>
          i !== idx &&
          row.stockCategoryId &&
          String(row.stockCategoryId) === String(m.stockCategoryId) &&
          row.lotId,
      )
      .map((row) => String(row.lotId));

    return baseOpts.filter(
      (opt) => !otherSelectedLotIds.includes(String(opt.value)),
    );
  };

  const labelClass =
    "text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1 block";

  if (isLoading)
    return (
      <div className="p-10 text-center text-slate-500">
        Loading Invoice details...
      </div>
    );
  if (isLocked) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/40">
          <h2 className="text-lg font-black uppercase tracking-tight text-amber-800 dark:text-amber-300">
            Invoice Locked
          </h2>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
            This invoice has already been marked as received, so editing is
            disabled.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(basePath)}
          className="shadow-sm hover:shadow-md transition-all"
        >
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4 max-w-[1600px] mx-auto animate-in fade-in duration-500">
      {/* --- Preview Modal --- */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 w-full max-w-5xl h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800">
            <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
              <div>
                <h3 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
                  Invoice <span className="text-primary-600">Preview</span>
                </h3>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                  Generated Invoice for {formData.invoiceNo || "Draft"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = previewUrl;
                    link.download = `Invoice_${formData.invoiceNo}.pdf`;
                    link.click();
                  }}
                  variant="primary"
                  size="md"
                  className="h-10 w-10 p-0 flex items-center justify-center rounded-xl text-lg"
                  title="Download PDF"
                >
                  <AiOutlineDownload className="text-xl" />
                </Button>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    if (justSaved) navigate(basePath);
                  }}
                  className="h-10 w-10 flex items-center justify-center rounded-xl bg-white dark:bg-slate-800 text-slate-400 hover:text-rose-500 transition-all border border-slate-200 dark:border-slate-700 hover:border-rose-200"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100 dark:bg-slate-950 p-4 sm:p-8 overflow-hidden">
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-2xl shadow-inner border border-slate-200 dark:border-slate-800"
                title="Invoice Preview"
              ></iframe>
            </div>
          </div>
        </div>
      )}


      {/* --- Page Header --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-800 pb-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 dark:bg-primary-500/20">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tight">
                {formMode === "edit" ? "Edit" : "Add"}{" "}
                <span className="text-primary-600">Invoice</span>
              </h1>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-0 flex items-center gap-2">
                Invoice Management{" "}
                <span className="h-1 w-1 rounded-full bg-slate-300"></span>{" "}
                Selling Master{" "}
                <span className="h-1 w-1 rounded-full bg-slate-300"></span>{" "}
                {formMode === "edit" ? "Edit Record" : "New Entry"}
              </p>
            </div>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(basePath)}
          className="group shadow-sm hover:shadow-md transition-all active:scale-95"
        >
          <svg
            className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to List
        </Button>
      </div>

      {formError && <ErrorAlert message={formError} />}

      <form onSubmit={handleSave} className="space-y-4">
        <fieldset
          disabled={formMode === "view"}
          className={
            formMode === "view"
              ? "pointer-events-none select-none opacity-80 space-y-4"
              : "space-y-4"
          }
        >
          {/* --- Section 1: Invoice Details --- */}
          <div className="group rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm transition-all  dark:border-slate-800 dark:bg-slate-900/50">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 font-black text-sm">
                01
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-800 dark:text-white">
                  Invoice Information
                </h2>
                <p className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                  Basic details for the invoices
                </p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
              <Input
                label="Invoice No"
                name="invoiceNo"
                value={formData.invoiceNo}
                onChange={handleInputChange}
                error={fieldErrors.invoiceNo}
                labelClassName={labelClass}
                placeholder="Enter Invoice No"
                className="bg-slate-50/50 dark:bg-slate-800/50"
              />
              <DatePicker
                label="Invoice Date"
                value={formData.date}
                onChange={(date) => setFormData({ ...formData, date })}
                error={fieldErrors.date}
                labelClassName={labelClass}
              />
              <SearchableDropdown
                label="Company"
                name="companyId"
                value={formData.companyId}
                options={options.companies}
                onChange={handleInputChange}
                error={fieldErrors.companyId}
                labelClassName={labelClass}
                onAdd={() => openModal("company")}
              />
              <SearchableDropdown
                label="Mode/Terms of Payment"
                name="termsOfPayment"
                value={formData.termsOfPayment}
                options={termsOfPaymentOptions}
                onChange={(e) =>
                  setFormData({ ...formData, termsOfPayment: e.target.value })
                }
                labelClassName={labelClass}
              />

              <DatePicker
                label="Due Date"
                value={formData.dueDate}
                onChange={(date) => setFormData({ ...formData, dueDate: date })}
                labelClassName={labelClass}
              />
              <DatePicker
                label="Delivery Date"
                value={formData.deliveryDate}
                onChange={(date) =>
                  setFormData({ ...formData, deliveryDate: date })
                }
                labelClassName={labelClass}
              />
              <SearchableDropdown
                label="Customer"
                name="customerId"
                value={formData.customerId}
                options={options.customers}
                onChange={handleInputChange}
                error={fieldErrors.customerId}
                labelClassName={labelClass}
                onAdd={() => openModal("customer")}
              />
              <Input
                label="Vehicle No"
                name="vehicleNo"
                value={formData.vehicleNo}
                onChange={handleInputChange}
                labelClassName={labelClass}
                placeholder="e.g. GJ-01-XX-0000"
                maxLength={12}
                className="bg-slate-50/50 dark:bg-slate-800/50"
              />
            </div>
            <div className="mt-4 space-y-2">
              <TextArea
                label="Memo"
                name="memo"
                value={formData.memo}
                onChange={handleInputChange}
                labelClassName={labelClass}
                placeholder="Enter internal notes, reminders, or delivery instructions..."
                rows={3}
              />
            </div>
            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
              <FilePreviewField
                label="Invoice Attachment"
                value={attachment}
                error={fieldErrors.attachment}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(file) => {
                  if (file) {
                    const fileError = validateFileSize(file);
                    setFieldErrors((prev) => ({
                      ...prev,
                      attachment: fileError,
                    }));
                    if (!fileError) setAttachment(file);
                  } else {
                    setAttachment(null);
                    setFieldErrors((prev) => ({ ...prev, attachment: null }));
                  }
                }}
              />
            </div>
          </div>

          {/* --- Section 2: Unified Transaction Details --- */}
          <div className="group  rounded-2xl border border-slate-200 bg-white shadow-sm transition-all  dark:border-slate-800 dark:bg-slate-900/50 overflow-hidden">
            {/* Header */}
            <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 font-black text-sm">
                  02
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-800 dark:text-white">
                    Transaction Details
                  </h2>
                  <p className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                    Manage items, taxes, and final billing
                  </p>
                </div>
              </div>
              {formMode !== "view" && (
                <Button
                  size="sm"
                  type="button"
                  onClick={addMaterial}
                  className="shadow-lg shadow-primary-500/10 active:scale-95 transition-transform"
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Add Item
                </Button>
              )}
            </div>

            {/* Line Items Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[1250px]">
                <thead>
                  <tr className="bg-slate-50/80 dark:bg-slate-800/80 text-[9px] font-black uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="px-3 py-2">Stock Category</th>
                    <th className="px-3 py-2">Lot</th>
                    <th className="px-3 py-2 min-w-[140px]">Yard</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Available</th>
                    <th className="px-3 py-2 text-right w-48">Quantity (KG)</th>
                    <th className="px-3 py-2 text-right w-24">Rate/KG</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right min-w-[130px]">
                      GST %
                    </th>
                    <th className="px-3 py-2 text-right">GST Amount</th>
                    <th className="px-3 py-2 text-right">Grand Total</th>
                    <th className="px-3 py-2 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {materials.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-6 py-20 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3 grayscale opacity-40">
                          <svg
                            className="h-12 w-12"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                          </svg>
                          <p className="text-sm font-bold uppercase tracking-widest">
                            No items added yet
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    materials.map((m, idx) => (
                      <tr
                        key={idx}
                        className="group/row hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="px-2 py-1.5 min-w-[180px]">
                          <SearchableDropdown
                            value={m.stockCategoryId}
                            options={getFilteredCategoryOptions(idx, m)}
                            onChange={(e) =>
                              handleMaterialChange(
                                idx,
                                "stockCategoryId",
                                e.target.value,
                              )
                            }
                            placeholder="Select Category"
                            onAdd={() => openModal("stockCategory")}
                          />
                        </td>
                        <td className="px-2 py-1.5 min-w-[140px]">
                          <SearchableDropdown
                            value={m.lotId}
                            options={getFilteredLotOptions(idx, m)}
                            onChange={(e) =>
                              handleMaterialChange(idx, "lotId", e.target.value)
                            }
                            placeholder="Select Lot"
                            onAdd={() => openModal("lot")}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-baseline justify-start gap-1">
                            <span className="text-xs font-black tracking-tight text-slate-800 dark:text-white">
                              {options.yards.find(
                                (y) => String(y.value) === String(m.yardId),
                              )?.label || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 min-w-[150px]">
                          <input
                            type="text"
                            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold outline-none ring-primary-500/10 transition-all focus:border-primary-500 focus:bg-white focus:ring-4 dark:bg-slate-800 dark:border-slate-700 dark:focus:border-primary-500 dark:focus:bg-slate-900 h-8"
                            value={m.description || ""}
                            onChange={(e) =>
                              handleMaterialChange(
                                idx,
                                "description",
                                e.target.value,
                              )
                            }
                            placeholder="Enter Description"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex flex-col items-end">
                            <div className="flex items-baseline justify-end gap-1">
                              <span
                                className={`text-xs font-black tracking-tight ${Number(m.availableQuantity) <= 0 ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"}`}
                              >
                                {Number(
                                  m.availableQuantity || 0,
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                              <span className="text-[9px] font-bold text-slate-400 uppercase">
                                KG
                              </span>
                            </div>
                            {(() => {
                              const hasSavedId = Boolean(m.sellingMaterialsId);
                              const isSame =
                                hasSavedId &&
                                String(m.lotId) === String(m.originalLotId) &&
                                String(m.stockCategoryId) ===
                                  String(m.originalStockCategoryId);

                              if (
                                isSame &&
                                parseFloat(m.originalQuantity || 0) > 0
                              ) {
                                const physicalStock =
                                  parseFloat(m.availableQuantity || 0) -
                                  parseFloat(m.originalQuantity || 0);
                                return (
                                  <div className="text-[9px] text-slate-400 dark:text-slate-500 font-semibold italic mt-0.5 leading-none">
                                    (
                                    {Number(physicalStock).toLocaleString(
                                      "en-IN",
                                      { minimumFractionDigits: 2 },
                                    )}{" "}
                                    +{" "}
                                    {Number(m.originalQuantity).toLocaleString(
                                      "en-IN",
                                      { minimumFractionDigits: 2 },
                                    )}{" "}
                                    saved)
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 w-48">
                          <input
                            type="number"
                            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-right font-bold outline-none ring-primary-500/10 transition-all focus:border-primary-500 focus:bg-white focus:ring-4 dark:bg-slate-800 dark:border-slate-700 dark:focus:border-primary-500 dark:focus:bg-slate-900 h-8"
                            value={m.quantity}
                            onChange={(e) =>
                              handleMaterialChange(
                                idx,
                                "quantity",
                                e.target.value,
                              )
                            }
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-2 py-1.5 w-24">
                          <input
                            type="number"
                            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-right font-bold outline-none ring-primary-500/10 transition-all focus:border-primary-500 focus:bg-white focus:ring-4 dark:bg-slate-800 dark:border-slate-700 dark:focus:border-primary-500 dark:focus:bg-slate-900 h-8"
                            value={m.ratePerKg}
                            onChange={(e) =>
                              handleMaterialChange(
                                idx,
                                "ratePerKg",
                                e.target.value,
                              )
                            }
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex items-baseline justify-end gap-2">
                            <span className="text-sm font-black text-slate-800 dark:text-white">
                              {Number(
                                calculateSellingMaterialTotals(m).amount,
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                            <span className="text-xs font-bold text-slate-400 tracking-tighter">
                              ₹
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 min-w-[130px]">
                          {!m.isCustomGst &&
                          GST_OPTIONS.includes(Number(m.gstPercentage)) ? (
                            <SearchableDropdown
                              value={Number(m.gstPercentage)}
                              options={GST_DROPDOWN_OPTIONS}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "custom") {
                                  const updated = [...materials];
                                  updated[idx].isCustomGst = true;
                                  setMaterials(updated);
                                } else {
                                  handleMaterialChange(
                                    idx,
                                    "gstPercentage",
                                    val,
                                  );
                                }
                              }}
                              placeholder="Select GST"
                            />
                          ) : (
                            <div className="relative flex items-center w-full">
                              <input
                                type="text"
                                inputMode="decimal"
                                className="w-full rounded-lg border border-slate-200 bg-slate-50 pl-2.5 pr-9 py-1 text-xs text-right font-bold outline-none ring-primary-500/10 transition-all focus:border-primary-500 focus:bg-white focus:ring-4 dark:bg-slate-800 dark:border-slate-700 dark:focus:border-primary-500 dark:focus:bg-slate-900 h-8"
                                value={m.gstPercentage}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    /^[0-9]*\.?[0-9]*$/.test(val)
                                  ) {
                                    handleMaterialChange(
                                      idx,
                                      "gstPercentage",
                                      val,
                                    );
                                  }
                                }}
                                placeholder="0.00"
                              />
                              <span className="absolute right-6 text-xs font-bold text-slate-400 pointer-events-none select-none">
                                %
                              </span>
                              <button
                                type="button"
                                title="Select from list"
                                onClick={() => {
                                  const updated = [...materials];
                                  updated[idx].isCustomGst = false;
                                  if (
                                    !GST_OPTIONS.includes(
                                      Number(updated[idx].gstPercentage),
                                    )
                                  ) {
                                    updated[idx].gstPercentage = 18;
                                  }
                                  const totals = calculateSellingMaterialTotals(
                                    updated[idx],
                                  );
                                  updated[idx].amount =
                                    totals.amount.toFixed(2);
                                  updated[idx].gstAmount =
                                    totals.gstAmount.toFixed(2);
                                  updated[idx].grandTotal =
                                    totals.grandTotal.toFixed(2);
                                  setMaterials(updated);
                                }}
                                className="absolute right-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 flex items-center justify-center rounded-md hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                              >
                                <svg
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex items-baseline justify-end gap-2">
                            <span className="text-sm font-black text-amber-600 dark:text-amber-400">
                              {Number(
                                calculateSellingMaterialTotals(m).gstAmount,
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                            <span className="text-xs font-bold text-slate-400 tracking-tighter">
                              ₹
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex items-baseline justify-end gap-2">
                            <span className="text-sm font-black text-primary-600 dark:text-primary-400">
                              {Number(
                                calculateSellingMaterialTotals(m).grandTotal,
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                            <span className="text-xs font-bold text-slate-400 tracking-tighter">
                              ₹
                            </span>
                          </div>
                        </td>
                        {formMode !== "view" && (
                          <td className="px-3 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={() => removeMaterial(idx)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-red-400 hover:bg-rose-70 hover:text-red-600 dark:text-slate-600 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 transition-all active:scale-90"
                            >
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Taxation, Adjustments & Total Summary */}
            <div className="bg-slate-50/50 dark:bg-slate-800/30 p-3 sm:p-5 border-t border-slate-100 dark:border-slate-800 space-y-4">
              {/* Taxation Inputs - Right Aligned */}
              <div className="flex flex-col md:flex-row gap-3 md:justify-end items-start md:items-end">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-800/30 rounded-lg animate-in fade-in slide-in-from-right-1 duration-500">
                  <i className="material-icons text-primary-600 text-sm">
                    info
                  </i>
                  <p className="text-[10px] font-bold text-primary-700 dark:text-primary-400 uppercase tracking-tight leading-tight">
                    Note: GST Amount is calculated from each item GST %.
                  </p>
                </div>

                <div className="w-full md:w-1/2 lg:w-1/3 space-y-2">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 text-right">
                    Taxation & Adjustments
                  </h3>
                  <table className="w-full border-separate border-spacing-y-1">
                    <tbody>
                      <tr>
                        <td className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pr-4 text-right">
                          GST Amount
                        </td>
                        <td className="w-1/2">
                          <Input
                            type="number"
                            name="gstAmount"
                            value={formData.gstAmount}
                            onChange={handleInputChange}
                            disabled={true}
                            suffix={
                              <span className="text-[10px] font-bold text-slate-400 pointer-events-none select-none">
                                ₹
                              </span>
                            }
                            className="bg-slate-100 dark:bg-slate-800 font-bold h-8 text-xs text-right cursor-not-allowed pr-8"
                          />
                        </td>
                      </tr>

                      <tr>
                        <td className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pr-4 text-right">
                          Round Off
                        </td>
                        <td>
                          <Input
                            type="number"
                            name="roundOffAmount"
                            value={formData.roundOffAmount}
                            onChange={handleInputChange}
                            disabled={true}
                            suffix={
                              <span className="text-[10px] font-bold text-slate-400 pointer-events-none select-none">
                                ₹
                              </span>
                            }
                            className="bg-slate-100 dark:bg-slate-800 font-bold h-8 text-xs text-right cursor-not-allowed pr-8"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Final Summary Bar - Bottom */}
            <div className="pt-4 p-4 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-6">
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    Subtotal Amount
                  </span>
                  <span className="text-lg font-bold text-slate-700 dark:text-slate-300">
                    {materials
                      .reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0)
                      .toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex flex-col border-l border-slate-200 dark:border-slate-800 pl-3 sm:pl-6">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    Total Items
                  </span>
                  <span className="text-lg font-bold text-slate-700 dark:text-slate-300">
                    {materials.length}{" "}
                    <span className="text-xs text-slate-400 font-medium">
                      Items
                    </span>
                  </span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-3 sm:gap-6 w-full md:w-auto">
                <div className="flex flex-col items-center md:items-end">
                  <span className="text-[10px] font-black text-primary-500 uppercase tracking-widest mb-1">
                    Grand Total (Payable)
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-black text-slate-800 dark:text-white tracking-tight">
                      {Number(formData.grandTotal).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                    <span className="text-xs font-bold text-slate-400">
                      INR
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handlePreview}
                    className="w-full sm:w-auto h-10 px-4 text-xs font-bold uppercase tracking-widest active:scale-95 transition-all"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    Preview
                  </Button>
                  {formMode !== "view" && (
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isSaving}
                      className="w-full sm:w-auto px-8 h-10 text-xs font-bold uppercase tracking-widest shadow-xl shadow-primary-500/20 active:scale-95 transition-all"
                    >
                      {formMode === "edit" ? "Update Invoice" : "Add Invoice"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate(basePath)}
                    disabled={isSaving}
                    className="w-full sm:w-auto h-10 px-4 text-xs font-bold uppercase"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </form>
      {renderModals()}
    </div>
  );
}

export default SellingMasterFormPage;
